/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BallotOutlinedIcon from "@mui/icons-material/BallotOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import classes from "./PageTitle.module.css";
import { useBreadcrumbData } from "../../hooks/useBreadcrumbData";

export interface BreadcrumbParentPage {
  label: string;
  to: string;
}

export interface BreadcrumbProps {
  pageHeader: string;
  systemOrProjectHeader?: string;
  parentPages?: BreadcrumbParentPage[];
  isDataProject?: boolean;
  isSyncPage?: boolean;
  isEnrichPage?: boolean;
  isMonitorPage?: boolean;
  integratedSystemId?: number;
  dataProjectId?: number;
  projectsLinkOverride?: string;
  systemLinkOverride?: string;
}

/**
 * URL patterns for old and new UI
 */
const URL_PATTERNS = {
  oldUI: {
    dashboard: (id: number) => `/app/dashboard/${id}`,
    monitor: (id: number) => `/app/contact-monitor/${id}`,
    dataProjectDashboard: (id: number) => `/app/data-project/${id}`,
    dataProjectMonitor: (id: number) =>
      `/app/contact-monitor/data-project/${id}`,
    projectsList: (id: number) => `/app/data-projects/${id}`,
    projectsListMonitor: (id: number) => `/app/data-projects/monitor/${id}`,
    sync: (id: number) => `/app/sync/${id}`,
  },
  newUI: {
    dashboard: (id: number) => `/product/dashboard/${id}`,
    monitor: (id: number) => `/product/contact-monitor/${id}`,
    dataProjectDashboard: (id: number) => `/data-project/dashboard/${id}`,
    enrichDataProjectDashboard: (id: number) => `/data-project/enrich/${id}`,
    dataProjectMonitor: (id: number) => `/data-project/contact-monitor/${id}`,
    projectsList: (id: number) => `/product/data-projects/${id}`,
    enrichProjectsList: (id: number) => `/product/data-projects/enrich/${id}`,
    projectsListMonitor: (id: number) => `/product/data-projects/monitor/${id}`,
    sync: (id: number) => `/app/sync/${id}`,
  },
};

interface LinkParams {
  isDataProject?: boolean;
  isSyncPage?: boolean;
  isEnrichPage?: boolean;
  isMonitorPage?: boolean;
  targetId?: number;
  isSystemLink?: boolean;
  isProjectsLink?: boolean;
}

/**
 * Generates the appropriate navigation link based on the context
 */
const getLinkByCirromFlow = (params: LinkParams, isOldUi: boolean): string => {
  const {
    isDataProject,
    isSyncPage,
    isEnrichPage,
    isMonitorPage,
    targetId,
    isSystemLink,
    isProjectsLink,
  } = params;

  const patterns = isOldUi ? URL_PATTERNS.oldUI : URL_PATTERNS.newUI;
  const id = targetId ?? 0;

  if (isSystemLink) {
    return isMonitorPage ? patterns.monitor(id) : patterns.dashboard(id);
  }

  if (isProjectsLink) {
    if (isEnrichPage) {
      return URL_PATTERNS.newUI.enrichProjectsList(id);
    }
    return isMonitorPage
      ? patterns.projectsListMonitor(id)
      : patterns.projectsList(id);
  }

  if (isDataProject) {
    if (isEnrichPage) {
      return URL_PATTERNS.newUI.enrichDataProjectDashboard(id);
    }
    return isMonitorPage
      ? patterns.dataProjectMonitor(id)
      : patterns.dataProjectDashboard(id);
  }

  if (isSyncPage) {
    return patterns.sync(id);
  }

  if (isMonitorPage) {
    return patterns.monitor(id);
  }

  return patterns.dashboard(id);
};

/**
 * Individual breadcrumb item with icon and link
 */
interface BreadcrumbItemProps {
  icon: React.ReactNode;
  to: string;
  label: string;
  ariaLabel?: string;
}

const BreadcrumbItem = ({
  icon,
  to,
  label,
  ariaLabel,
}: BreadcrumbItemProps) => (
  <>
    {icon}
    <Link to={to} className={classes.breadcrumbItem} aria-label={ariaLabel}>
      {label}
    </Link>
    <NavigateNextIcon fontSize="small" aria-hidden="true" />
  </>
);

/**
 * Breadcrumb Component
 *
 * A reusable breadcrumb navigation component that supports:
 * - Simple system/project navigation: System Name > Page Title
 * - Nested pages via `parentPages`: System Name > Parent Page > Page Title
 * - Enhanced data project navigation: System Name > Projects > Data Project Name > Page Title
 */
export const Breadcrumb = React.memo(function Breadcrumb(
  props: Readonly<BreadcrumbProps>
) {
  const {
    pageHeader,
    systemOrProjectHeader,
    isDataProject,
    isSyncPage,
    isEnrichPage,
    isMonitorPage,
    integratedSystemId,
    dataProjectId,
    projectsLinkOverride,
    systemLinkOverride,
    parentPages,
  } = props;

  const breadcrumbData = useBreadcrumbData(isDataProject);
  const isOldUi = useMemo(() => window.location.href.includes("app"), []);

  const parentSystemName = breadcrumbData.parentSystemName;
  const parentSystemId = breadcrumbData.parentSystemId;

  const systemIcon = (
    <AssignmentOutlinedIcon
      fontSize="small"
      aria-hidden="true"
      className={classes.breadcrumbIcon}
    />
  );

  const projectIcon = (
    <BallotOutlinedIcon
      fontSize="small"
      aria-hidden="true"
      className={classes.breadcrumbIcon}
    />
  );

  const folderIcon = (
    <FolderOutlinedIcon
      fontSize="small"
      aria-hidden="true"
      className={classes.breadcrumbIcon}
    />
  );

  if (!systemOrProjectHeader) {
    return null;
  }

  if (isDataProject && parentSystemName && parentSystemId) {
    const systemLink = getLinkByCirromFlow(
      {
        targetId: parentSystemId,
        isSystemLink: true,
        isSyncPage,
        isMonitorPage,
        isEnrichPage,
      },
      isOldUi
    );

    const projectsLink =
      projectsLinkOverride ||
      getLinkByCirromFlow(
        {
          targetId: parentSystemId,
          isProjectsLink: true,
          isMonitorPage,
          isEnrichPage,
        },
        isOldUi
      );

    const projectLink = getLinkByCirromFlow(
      {
        isDataProject: true,
        isMonitorPage,
        isEnrichPage,
        targetId: dataProjectId || integratedSystemId,
      },
      isOldUi
    );

    return (
      <nav aria-label="Breadcrumb">
        {/* This trail ends in the page's own name and is the largest text on
            the page, so it is the page heading. Without `component`, MUI
            renders it as an <h3> and the page has no <h1> at all. */}
        <Typography
          color="textPrimary"
          variant="h3"
          component="h1"
          className={classes.breadcrumb}
        >
          <BreadcrumbItem
            icon={systemIcon}
            to={systemLink}
            label={parentSystemName}
          />
          <BreadcrumbItem
            icon={folderIcon}
            to={projectsLink}
            label="Projects"
            ariaLabel={`${parentSystemName} Projects`}
          />
          <BreadcrumbItem
            icon={projectIcon}
            to={projectLink}
            label={systemOrProjectHeader}
          />
          <span aria-current="page">{pageHeader}</span>
        </Typography>
      </nav>
    );
  }

  // Standard breadcrumb: System/Project Name > Page Title
  const link =
    systemLinkOverride ||
    getLinkByCirromFlow(
      {
        isDataProject,
        isSyncPage,
        isEnrichPage,
        isMonitorPage,
        targetId: integratedSystemId,
      },
      isOldUi
    );

  return (
    <nav aria-label="Breadcrumb">
      <Typography
        color="textPrimary"
        variant="h3"
        component="h1"
        className={classes.breadcrumb}
      >
        <BreadcrumbItem
          icon={isDataProject ? projectIcon : systemIcon}
          to={link}
          label={systemOrProjectHeader}
        />
        {parentPages?.map((parent) => (
          <BreadcrumbItem
            key={parent.to}
            icon={folderIcon}
            to={parent.to}
            label={parent.label}
          />
        ))}
        <span aria-current="page">{pageHeader}</span>
      </Typography>
    </nav>
  );
});
